import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { toast } from "sonner";

import { criarClassificacao, listarClassificacoes, listarDespesas, obterDespesa, salvarDespesa } from "./api";
import { deDespesa, paraApi, validar, VAZIO } from "./formulario";

import { nomesDosFornecedores } from "@/lib/fornecedores";
import { logger } from "@/utils/logger";

const reducer = (estado, acao) => (acao.tipo === "carregar" ? { ...acao.valor } : { ...estado, [acao.campo]: acao.valor });

// Estado e ações da tela Nova/Editar despesa. `id` presente = edição.
export default function useNovaDespesa(id) {
  const [form, dispatch] = useReducer(reducer, VAZIO);
  const [inicial, setInicial] = useState(VAZIO);
  const [classificacoes, setClassificacoes] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [status, setStatus] = useState({ carregando: true, salvando: false, original: null });

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [cls, todas, atual, salvos] = await Promise.all([
          listarClassificacoes(), listarDespesas(), id ? obterDespesa(id) : null, nomesDosFornecedores(),
        ]);
        if (!vivo) return;
        setClassificacoes(cls);
        setFornecedores([...new Set([...salvos, ...todas.map((e) => e.supplier).filter(Boolean)])].sort());
        if (atual) {
          const f = deDespesa(atual);
          dispatch({ tipo: "carregar", valor: f });
          setInicial(f);
        }
        setStatus((s) => ({ ...s, carregando: false, original: atual }));
      } catch (erro) {
        logger.error("[DESPESA] erro ao abrir a tela:", erro);
        toast.error(erro?.response?.status === 404 ? "Despesa não encontrada" : "Erro ao carregar a despesa");
        if (vivo) setStatus((s) => ({ ...s, carregando: false }));
      }
    })();
    return () => { vivo = false; };
  }, [id]);

  const definir = useCallback((campo, valor) => dispatch({ campo, valor }), []);
  const alterado = useMemo(() => JSON.stringify(form) !== JSON.stringify(inicial), [form, inicial]);

  const novaClassificacao = useCallback(async (nome, natureza) => {
    if (!nome.trim()) { toast.error("Informe o nome da classificação"); return false; }
    if (!natureza) { toast.error("Escolha a natureza da classificação"); return false; }
    try {
      const criada = await criarClassificacao(nome, natureza);
      setClassificacoes(await listarClassificacoes());
      definir("classificationId", criada.id);
      toast.success("Classificação criada!");
      return true;
    } catch (erro) {
      toast.error(erro?.response?.data?.detail || "Erro ao criar classificação");
      return false;
    }
  }, [definir]);

  const salvar = useCallback(async () => {
    const erro = validar(form);
    if (erro) { toast.error(erro); return false; }
    setStatus((s) => ({ ...s, salvando: true }));
    try {
      await salvarDespesa(id, paraApi(form));
      const parcelas = parseInt(form.installments, 10) || 0;
      toast.success(id ? "Despesa atualizada!"
        : form.isRecurring ? `Despesa recorrente criada! ${form.recurringCount} repetições geradas.`
          : parcelas > 1 ? `Despesa parcelada criada! ${parcelas} parcelas geradas.` : "Despesa criada!");
      setInicial(form);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar despesa");
      return false;
    } finally {
      setStatus((s) => ({ ...s, salvando: false }));
    }
  }, [form, id]);

  return { form, definir, classificacoes, fornecedores, novaClassificacao, salvar, alterado, ...status };
}
